import {Component, ComponentFactoryResolver, Inject, OnInit} from '@angular/core';
import {MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';
import {RailgunBase} from 'src/app/RailgunBase';
import {HtmlMailService} from 'src/app/Service/HtmlMailService';
import {HtmlMailImageService} from 'src/app/Service/HtmlMailImageService';
import {HtmlMailDTOInterface} from 'src/app/DTO/HtmlMailDTOInterface';
import {HtmlMailImageDTOInterface} from 'src/app/DTO/HtmlMailImageDTOInterface';

/**
 * HtmlMailCreatorPopoverComponent
 * HTMLメール作成・編集ポップオーバー
 *
 * 呼び出し方：
 * this.showDialogWindow(
 *   HtmlMailCreatorPopoverComponent,
 *   {
 *     mode: 'create', // または 'edit'
 *     htmlMail: existingHtmlMail, // 編集時のみ
 *     close: this.onHtmlMailCreated.bind(this)
 *   }
 * );
 *
 * closeメソッドは以下の引数で用意する：
 * onHtmlMailCreated(result: { success: boolean, htmlMail?: HtmlMailDTOInterface })
 */
// LINE対応として登録する元画像の条件（メール専用登録にはこの制限を課さない）。
const LINE_IMAGE_FILE_TYPES = ['image/jpeg', 'image/png'];
const LINE_IMAGE_MAX_FILE_SIZE = 10 * 1024 * 1024;

@Component({
	selector: 'html-mail-creator-popover',
	templateUrl: './HtmlMailCreatorPopoverComponent.html',
	styleUrls: ['./HtmlMailCreatorPopoverComponent.scss']
})
export class HtmlMailCreatorPopoverComponent extends RailgunBase implements OnInit {

	public isLoading = false;
	public imageLinkUrl = '';
	public imageAltText = '';
	public isEditMode = false;

	// フォームデータ
	public formData: HtmlMailDTOInterface = {
		id: 0,
		name: '',
		subject: '',
		content: ''
	};

	// 画像一覧
	public imageList: Array<HtmlMailImageDTOInterface> = [];

	// エラーメッセージ
	public errorMessages: string[] = [];

	private htmlMailService: HtmlMailService;
	private htmlMailImageService: HtmlMailImageService;

	constructor(
		public resolver: ComponentFactoryResolver,
		public dialogRef: MatDialogRef<HtmlMailCreatorPopoverComponent>,
		@Inject(MAT_DIALOG_DATA) public data: any
	) {
		super();
		this.isEditMode = data.mode === 'edit';
		this.htmlMailService = new HtmlMailService(this);
		this.htmlMailImageService = new HtmlMailImageService(this);
	}

	ngOnInit(): void {
		this.start();
	}

	public async start() {
		await this.initializeForm();
		await this.loadImageList();
	}

	/**
	 * フォームの初期化
	 */
	private async initializeForm() {
		this.isLoading = true;

		if (this.isEditMode && this.data.htmlMail) {
			this.formData = {...this.data.htmlMail};
		}

		this.isLoading = false;
	}

	/**
	 * 画像一覧の読み込み
	 * 一覧非表示にした画像は選択肢に出さない。
	 */
	public async loadImageList() {
		const list = await this.htmlMailImageService.getActiveList();
		if (list) {
			this.imageList = list;
		}
	}

	/**
	 * 入力値検証
	 */
	private validateInput(): string[] {
		const errors: string[] = [];

		if (!this.formData.name || this.formData.name.trim().length === 0) {
			errors.push('テンプレート名が入力されていません。');
		}

		if (!this.formData.subject || this.formData.subject.trim().length === 0) {
			errors.push('件名が入力されていません。');
		}

		if (!this.formData.content || this.formData.content.trim().length === 0) {
			errors.push('本文が入力されていません。');
		}

		return errors;
	}

	/**
	 * 保存処理
	 */
	public async onSave() {
		this.isLoading = true;
		this.errorMessages = [];

		const validationErrors = this.validateInput();
		if (validationErrors.length > 0) {
			this.errorMessages = validationErrors;
			this.isLoading = false;
			return;
		}

		this.data.close({ success: true, htmlMail: this.formData });
		this.isLoading = false;
	}

	/**
	 * キャンセル処理
	 */
	public onCancel() {
		this.data.close({ success: false });
	}

	/**
	 * 画像アップロード処理
	 */
	public async onImageUpload(event: Event) {
		const input = event.target as HTMLInputElement;
		if (!input.files) {
			return;
		}
		if (input.files.length === 0) {
			return;
		}

		if (this.isLoading) {
			return;
		}
		this.errorMessages = [];
		const file = input.files[0];

		// リンクURL・ALTはどちらも未入力ならメール専用として登録する。
		// 片方だけの指定はAPIが受け付けないため、ここで止める。
		const hasLink = !!this.imageLinkUrl.trim();
		const hasAlt = !!this.imageAltText.trim();
		if (hasLink !== hasAlt) {
			this.errorMessages = ['リンクURLとALTは両方を入力するか、両方を未入力にしてください。両方未入力の場合はメール専用の画像として登録します。'];
			input.value = '';
			return;
		}

		// LINE対応として登録する場合だけ、元画像の形式・サイズを先に確認する。
		if (hasLink) {
			if (!LINE_IMAGE_FILE_TYPES.includes(file.type)) {
				this.errorMessages = ['LINEに対応する画像はJPEGまたはPNGを選択してください。'];
				input.value = '';
				return;
			}
			if (file.size > LINE_IMAGE_MAX_FILE_SIZE) {
				this.errorMessages = ['LINEに対応する画像は10MB以内で選択してください。'];
				input.value = '';
				return;
			}
		}

		this.isLoading = true;
		try {
			const uploaded = await this.htmlMailImageService.upload(file, this.imageLinkUrl.trim(), this.imageAltText.trim());
			if (uploaded) {
				await this.loadImageList();
				this.imageLinkUrl = '';
				this.imageAltText = '';
			} else {
				let message = '画像のアップロードに失敗しました。';
				const error = this.getErrorObj();
				if (error) {
					message = error.getMessage();
				}
				this.errorMessages = [message];
				this.setErrorObj(null);
			}
		} finally {
			input.value = '';
			this.isLoading = false;
		}
	}

	/**
	 * 画像削除処理
	 */
	public async onImageDelete(image: HtmlMailImageDTOInterface) {
		this.isLoading = true;
		const result = await this.htmlMailImageService.delete(image.id);

		if (result !== null) {
			await this.loadImageList();
		} else {
			this.errorMessages = ['画像の削除に失敗しました。'];
		}

		this.isLoading = false;
	}

	/**
	 * 画像プレースホルダーを本文に挿入
	 */
	public onInsertImagePlaceholder(image: HtmlMailImageDTOInterface) {
		const placeholder = `[img id="${image.id}"]`;
		this.formData.content = (this.formData.content || '') + placeholder;
	}

	public getDialogTitle(): string {
		return this.isEditMode ? 'HTMLメール編集' : 'HTMLメール作成';
	}

	public getSaveButtonLabel(): string {
		return this.isEditMode ? '更新' : '作成';
	}
}
